import type { MiddlewareState } from '@floating-ui/react-dom'

import type { Align, Side } from './types'

/**
 * Custom Floating UI middleware to optionally align the arrow to the start or
 * end of the reference element. Floating UI's `arrow` middleware only
 * supports centering the arrow on the reference element.
 */
export const arrowAlignment = (
  options: { align?: Align; element?: HTMLElement; padding?: number } = {}
) => ({
  name: 'arrowAlignment',
  options,
  fn: (state: MiddlewareState) => {
    const { x, y, rects, placement, middlewareData } = state
    const { align, element, padding = 0 } = options
    const { arrow } = middlewareData
    const { reference, floating } = rects
    const [side] = placement.split('-') as [Side, Align | undefined]

    const arrowLeft = (() => {
      if (side === 'left' || side === 'right') return undefined

      const elementWidth = element?.offsetWidth ?? 0

      // If the arrow + padding is wider than the reference, fallback to center
      // aligning the arrow to the reference.
      if (elementWidth + padding * 2 > reference.width) return arrow?.x

      switch (align) {
        case 'center':
          return arrow?.x
        case 'start':
          return Math.max(reference.x - x, 0) + padding
        case 'end':
          return (
            Math.min(reference.x - x + reference.width, floating.width) -
            elementWidth -
            padding
          )
      }
    })()

    const arrowTop = (() => {
      if (side === 'top' || side === 'bottom') return undefined

      const elementHeight = element?.offsetHeight ?? 0

      // If the arrow + padding is taller than the reference, fallback to center
      // aligning the arrow to the reference.
      if (elementHeight + padding * 2 > reference.height) return arrow?.y

      switch (align) {
        case 'center':
          return arrow?.y
        case 'start':
          return Math.max(reference.y - y, 0) + padding
        case 'end':
          return (
            Math.min(reference.y - y + reference.height, floating.height) -
            elementHeight -
            padding
          )
      }
    })()

    return {
      data: {
        x: arrowLeft,
        y: arrowTop,
      },
    }
  },
})
